import React, { FC, useState } from 'react';

import { useQuery } from 'react-apollo';
import { useHistory, useLocation } from 'react-router-dom';

import Button from 'Components-redesign/Button';
import HorizontalTabs from 'Components-redesign/HorizontalTabs';
import Loader from 'Components-redesign/Loader';
import { Heading } from 'Components-redesign/Typography/Heading';
import { Paragraph } from 'Components-redesign/Typography/Paragraph';
import { useI18n } from 'Hooks';
import { Box } from 'styles/box';

import { GetLatestReleases, GetUser } from './api/__queries__';
import { GET_LATEST_RELEASES, GET_USER } from './api/queries';
import FeatureForm from './Components/FeatureForm';
import FeaturesList from './Components/FeaturesList';
import ReleaseNoteForm from './Components/ReleaseNoteForm';
import ReleaseNotesList from './Components/ReleaseNotesList';
import { TabsWrapper, Wrapper } from './styles';
import { RELEASE_TYPE } from './types';

const useQueryParams = () => {
  return new URLSearchParams(useLocation().search);
};

const NewRelease: FC = () => {
  const { t } = useI18n();
  const history = useHistory();
  const queryParams = useQueryParams();

  const id = queryParams.get('id');
  const type = queryParams.get('type');

  const TABS = {
    FEATURES: 'Features',
    RELEASE_NOTES: 'Release Notes',
  };

  const userQuery = useQuery<GetUser>(GET_USER, {
    fetchPolicy: 'cache-and-network',
  });

  const featuresQuery = useQuery<GetLatestReleases>(GET_LATEST_RELEASES, {
    variables: {
      limit: 50,
      type: RELEASE_TYPE.FEATURE,
    },
  });

  const releaseNotesQuery = useQuery<GetLatestReleases>(GET_LATEST_RELEASES, {
    variables: {
      limit: 50,
      type: RELEASE_TYPE.RELEASE_NOTES,
    },
  });

  const user = userQuery.data?.User;
  const features = featuresQuery.data?.getLatestReleases.items || [];
  const releaseNotes = releaseNotesQuery.data?.getLatestReleases.items || [];

  const [selectedTab, setSelectedTab] = useState<string>('FEATURES');

  if (id && type === 'feature') {
    return (
      <FeatureForm
        id={id}
        username={user?.name ?? ''}
        refetch={featuresQuery.refetch}
        onComplete={() => setSelectedTab('FEATURES')}
      />
    );
  }

  if (id && type === 'release-notes') {
    return (
      <ReleaseNoteForm
        id={id}
        username={user?.name ?? ''}
        refetch={featuresQuery.refetch}
        onComplete={() => setSelectedTab('RELEASE_NOTES')}
      />
    );
  }

  return (
    <Wrapper>
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box>
          <Heading level={1}>{t('Releases.Title')}</Heading>
          <Paragraph level={3}>{t('Releases.Subtitle')}</Paragraph>
        </Box>

        <Box display="flex" gap="small" alignItems="center">
          <Button
            text={t('Releases.PublishNewFeature')}
            styleType="Dark"
            handleClick={() => {
              history.push('/releases?id=new&type=feature');
            }}
          />
          <Button
            text={t('Releases.PublishReleaseNotes')}
            styleType="OK"
            handleClick={() => {
              history.push('/releases?id=new&type=release-notes');
            }}
          />
        </Box>
      </Box>

      <TabsWrapper>
        <HorizontalTabs tabs={TABS} selectedTab={selectedTab} onSelect={(e) => setSelectedTab(e)} />
      </TabsWrapper>

      <Box display="flex" flexDirection="column" gap="small" marginTop="medium">
        {userQuery.loading || featuresQuery.loading || releaseNotesQuery.loading ? (
          <Box
            display="flex"
            marginTop="xl"
            flexDirection="column"
            justifyContent="center"
            alignItems="center"
            gap="small">
            <Loader color="var(--colorBlack)" />
            <Paragraph level={3}>{t('Releases.Loading.Features')}.</Paragraph>
          </Box>
        ) : selectedTab === 'FEATURES' ? (
          <FeaturesList features={features} />
        ) : selectedTab === 'RELEASE_NOTES' ? (
          <ReleaseNotesList releaseNotes={releaseNotes} />
        ) : (
          <div></div>
        )}
      </Box>
    </Wrapper>
  );
};

export default NewRelease;
